import React from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'

import Modal from '../../../UniformComponents/Modal'
import Typography from '../../../UniformComponents/Typography'
import Select from '../../form/Select'
import Checkbox from '../../form/Checkbox'
import Switch from '../../../UniformComponents/Switch'
import Button from '../../../UniformComponents/Button'
import Tooltip from '../../../UniformComponents/Tooltip'

const VerbatimSettingsModal = ({ store }) => {
    let warningText = null

    if (store.questionCodingAvailability.get(store.activeChart?.questionId) === false) {
        warningText = i18n.t('powerpoint.modals.verbatim.warning')
    }

    if (store.presentationCodingAvailability === 'survey') {
        warningText = i18n.t('powerpoint.modals.verbatim.survey-warning')
    }

    if (store.presentationCodingAvailability === 'client') {
        warningText = i18n.t('powerpoint.modals.verbatim.client-warning')
    }

    const isExploriStaff = store.appStore.userData?.flags?.isExploriStaff

    return (
        <Modal
            show={store.verbatimSettingsModalVisible}
            onClose={store.handleCloseVerbatimSettingsModal}
            id="ppt-verbatim-modal"
        >
            <Typography type="h3">{i18n.t('powerpoint.modals.verbatim.title')}</Typography>

            <br />
            {warningText && !isExploriStaff ? (
                <>
                    <Typography type="tiny" cssClass="pp-verbatim-modal-warning">
                        {warningText}
                    </Typography>

                    <br />
                    <Button
                        label={i18n.t('cancel')}
                        cssClass="regular tertiary"
                        onClick={store.handleCloseVerbatimSettingsModal}
                    />
                </>
            ) : (
                <>
                    <Typography type="tiny">
                        {i18n.t(store.activeChart?.questionCoding.categories.length > 1
                            ? 'powerpoint.modals.verbatim.instruction'
                            : 'powerpoint.modals.verbatim.instruction.no-categories',
                        { n: store.verbatimSlots })}
                    </Typography>

                    <br />

                    {store.activeChart?.questionCoding.categories.length > 1 ? (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                                <Select
                                    label="Category"
                                    options={store.activeChart?.codedCategories || []}
                                    onBlur={(e) => store.activeChart?.handleChooseCategory(e.value, e.pct, e.count)}
                                    value={store.activeChart?.pendingChanges.category}
                                    style={{ width: '100%' }}
                                />

                                <Select
                                    label="Colour Scheme"
                                    options={store.verbatimColourOptions || []}
                                    onBlur={(e) => store.activeChart?.handleChooseColour(e.value)}
                                    value={store.activeChart?.pendingChanges.colour}
                                    defaultValue={store.activeChart?.pendingChanges.verbatimColour || null}
                                    style={{ width: 300 }}
                                />
                            </div>

                            <br />

                            {!!store.activeChart?.pendingChanges.category && (
                                <div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div className="pp-verbatim-modal-control-header">
                                            <Typography type="b">
                                                {i18n.t('powerpoint.modals.verbatim.selections', { 1: store.verbatimSlots || 3 })}
                                            </Typography>
                                            <Typography type="tiny">
                                                {i18n.t('powerpoint.modals.verbatim.selectionCount',
                                                    {
                                                        1: store.activeChart?.pendingChanges?.labels?.length || 0,
                                                        2: store.verbatimSlots || 3,
                                                    })}
                                            </Typography>
                                        </div>

                                        <div className="pp-verbatim-modal-count-format">
                                            <span>#</span>
                                            <Switch
                                                onToggle={(isPct) => store.activeChart?.handleUpdateCountFormat({
                                                    value: isPct ? 'pct' : 'count',
                                                })}
                                                state={(store.activeChart?.countFormat || 'pct') === 'pct'}
                                                icon="fa-regular fa-check"
                                            />
                                            <span>%</span>
                                        </div>
                                        <Button
                                            label={i18n.t('powerpoint.modals.verbatim.clearSelections')}
                                            cssClass="regular secondary link-btn"
                                            disabled={!store.activeChart?.pendingChanges?.labels?.length}
                                            onClick={store.activeChart?.handleClearVerbatimSelections}
                                        />
                                    </div>

                                    <br />

                                    <div className="pp-verbatim-modal-checkbox-container">
                                        {!!store.activeChart?.pendingChanges.category && (
                                            store.activeChart?.labelsForCategory.map(({ label }) => {
                                                const isChecked = store.activeChart?.pendingChanges.labels.includes(label)
                                                return (
                                                    <div
                                                        onClick={(e) => {
                                                            e.preventDefault()
                                                            store.activeChart?.handleChooseLabel(label)
                                                        }}
                                                        className={
                                                            isChecked
                                                                ? 'pp-verbatim-modal-checkbox-selected'
                                                                : 'pp-verbatim-modal-checkbox'
                                                        }
                                                    >
                                                        <Checkbox
                                                            name={label}
                                                            checked={isChecked}
                                                            label={label}
                                                        />
                                                    </div>
                                                )
                                            })
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div>
                            <Button
                                label="Generate coding"
                                cssClass="regular primary"
                                onClick={store.activeChart?.generateCoding}
                            />
                        </div>
                    )}

                    <br />

                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            label={i18n.t('cancel')}
                            cssClass="regular tertiary"
                            onClick={store.activeChart?.handleCancelVerbatimChoices}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                            {(store.activeChart?.questionCoding.categories.length > 1
                                && !store.activeChart?.hasPickedComments)
                                && (
                                    <Tooltip
                                        isNew
                                        text={i18n.t('powerpoint.modals.verbatim.autofill_desc', { n: store.verbatimSlots })}
                                        direction="top"
                                    >
                                        <Button
                                            label={i18n.t('powerpoint.modals.verbatim.autofill_using_ai')}
                                            icon="fa-regular fa-sparkles"
                                            cssClass="regular secondary"
                                            disabled={store.activeChart?.isLoadingTopResponses
                                                || store.activeChart?.isRateLimited}
                                            onClick={store.activeChart?.handleAutoFillVerbatimSlots}
                                        />
                                    </Tooltip>
                                )}
                            <Button
                                label={i18n.t('save')}
                                cssClass="regular primary"
                                onClick={store.activeChart?.handleSaveVerbatimChoices}
                            />
                        </div>
                    </div>
                </>
            )}
        </Modal>
    )
}

export default observer(VerbatimSettingsModal)
